import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import {
    formatPrice,
    formatBalance,
    formatQuantity,
    formatAssetPrice,
    formatAmount,
    formatCompactNumber,
    calculatePercentageChange,
    calculatePriceChange,
    FormatOptions,
    FormattedPrice,
    AssetType,
} from '../utils/formatting';

interface UsePriceFormatterOptions {
    forceCompact?: boolean;
    forceSeparator?: boolean;
}

export const usePriceFormatter = (options: UsePriceFormatterOptions = {}) => {
    const {
        forceCompact = false,
        forceSeparator = true,
    } = options;

    // Get currency from Redux
    const currencyInfo = useSelector((state: RootState) => state.currency);
    const currencySymbol = currencyInfo.symbol || '$';

    const price = useCallback(
        (value: any, formatOptions?: FormatOptions): FormattedPrice => {
            return formatAssetPrice(value, currencySymbol, {
                compact: forceCompact,
                separator: forceSeparator,
                ...formatOptions,
            });
        },
        [currencySymbol, forceCompact, forceSeparator]
    );

    const balance = useCallback(
        (value: any): FormattedPrice => {
            return formatBalance(value, currencySymbol, {
                separator: forceSeparator,
            });
        },
        [currencySymbol, forceSeparator]
    );

    const quantity = useCallback(
        (value: any, assetType: AssetType = AssetType.CRYPTO): FormattedPrice => {
            return formatQuantity(value, assetType, {
                separator: forceSeparator,
            });
        },
        [forceSeparator]
    );

    const amount = useCallback(
        (value: any, formatOptions?: FormatOptions): FormattedPrice => {
            return formatAmount(value, currencySymbol, {
                separator: forceSeparator,
                ...formatOptions,
            });
        },
        [currencySymbol, forceSeparator]
    );

    const compact = useCallback(
        (value: any, decimals: number = 2): string => {
            return formatCompactNumber(value, decimals);
        },
        []
    );

    const percentChange = useCallback(
        (current: any, previous: any): string => {
            return calculatePercentageChange(current, previous);
        },
        []
    );

    const priceChange = useCallback(
        (current: any, previous: any, decimals?: number): string => {
            return calculatePriceChange(current, previous, decimals);
        },
        []
    );

    const display = useCallback(
        (value: any, formatOptions?: FormatOptions): string => {
            const result = formatPrice(value, {
                currencySymbol,
                smartDecimals: true,
                displayType: 'price',
                ...formatOptions,
            } as any);
            return result.display;
        },
        [currencySymbol]
    );

    return {
        price,        // Smart price formatting with currency symbol
        balance,      // Fiat balance (always 2 decimals)
        quantity,     // Smart quantity formatting (no symbol)
        amount,       // Smart amount formatting with currency symbol
        compact,      // Compact notation (K, M, B, T)
        percentChange,
        priceChange,
        display,
    };
};